import { useState, useRef } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
    Upload, X, Image as ImageIcon, Wine, Package, Coins,
    Ruler, Box, AlertCircle, Save, ChevronDown, ChevronUp,
} from "lucide-react";

const WINE_TYPES = [
    { value: "tinto", label: "Tinto" },
    { value: "branco", label: "Branco" },
    { value: "rose", label: "Rosé" },
    { value: "espumante", label: "Espumante" },
    { value: "porto", label: "Vinho do Porto" },
    { value: "madeira", label: "Madeira" },
    { value: "verde", label: "Verde" },
    { value: "moscatel", label: "Moscatel" },
];

const WINE_CATEGORIES = ["Vinho Tinto", "Vinho Branco", "Rosé", "Espumante", "Vinho do Porto", "Madeira", "Verde", "Moscatel"];
const PRODUCT_CATEGORIES = ["Azeites", "Queijos", "Enchidos", "Compotas & Mel", "Chocolates", "Conservas", "Doçaria", "Bolaria", "Frutos Secos"];

const REGIONS = ["Douro", "Alentejo", "Dão", "Vinho Verde", "Beira Alta", "Beira Litoral", "Bairrada", "Lisboa", "Madeira", "Açores", "Algarve", "Trás-os-Montes", "Estremadura", "Ribatejo"];

const PACKAGE_TYPES = {
    wine: ["Garrafa 750ml", "Garrafa 375ml", "Garrafa 1.5L", "Garrafa 3L (Magnum)", "Lata", "Bag-in-Box"],
    product: ["Frasco vidro", "Frasco plástico", "Lata", "Saco vácuo", "Caixa cartão", "Caixa madeira", "Embalagem flow-pack"],
};

const ALLERGENS = ["Glúten", "Lactose", "Frutos secos", "Sulfitos", "Ovos", "Soja", "Peixe", "Crustáceos", "Mostarda", "Aipo"];
const CERTIFICATIONS = ["DOP", "IGP", "DOC", "Bio / Orgânico", "Sem glúten", "Vegan", "Kosher", "Halal"];
const CLASSIFICATIONS = ["DOC", "DOP", "IGP", "Regional", "Vinho de Mesa"];

/**
 * Formulário profissional de produto (Vinho ou Artesanal).
 * @param {"wine"|"product"} kind
 * @param {object} initial - produto existente (para edição) ou null
 * @param {object} producer - perfil do produtor (para defaults inteligentes)
 * @param {function} onClose
 * @param {function} onSaved
 */
export default function VendorProductForm({ kind, initial, producer, onClose, onSaved }) {
    const isWine = kind === "wine";
    const [saving, setSaving] = useState(false);
    const defaultRegion = initial?.region || producer?.region || "Douro";

    const [form, setForm] = useState({
        name: initial?.name || "",
        short_description: initial?.short_description || "",
        description: initial?.description || "",
        category: initial?.category || (isWine ? "Vinho Tinto" : "Azeites"),
        region: defaultRegion,
        price: initial?.price ?? "",
        producer_price: initial?.producer_price ?? "",
        stock: initial?.stock ?? "",
        limited_edition: initial?.limited_edition || false,
        images: initial?.images || [],
        wine: {
            wine_type: initial?.wine?.wine_type || (isWine ? "tinto" : ""),
            vintage_year: initial?.wine?.vintage_year || "",
            alcohol_pct: initial?.wine?.alcohol_pct || "",
            grape_varieties: initial?.wine?.grape_varieties || [],
            classification: initial?.wine?.classification || "",
            bottle_volume_ml: initial?.wine?.bottle_volume_ml || 750,
            serving_temp: initial?.wine?.serving_temp || "",
            pairing: initial?.wine?.pairing || "",
        },
        product: {
            ingredients: initial?.product?.ingredients || "",
            allergens: initial?.product?.allergens || [],
            shelf_life_days: initial?.product?.shelf_life_days || "",
            certifications: initial?.product?.certifications || [],
            storage: initial?.product?.storage || "",
        },
        packaging: {
            unit_weight_kg: initial?.packaging?.unit_weight_kg || "",
            unit_length_cm: initial?.packaging?.unit_length_cm || "",
            unit_width_cm: initial?.packaging?.unit_width_cm || "",
            unit_height_cm: initial?.packaging?.unit_height_cm || "",
            box_units: initial?.packaging?.box_units || 1,
            box_weight_kg: initial?.packaging?.box_weight_kg || "",
            box_length_cm: initial?.packaging?.box_length_cm || "",
            box_width_cm: initial?.packaging?.box_width_cm || "",
            box_height_cm: initial?.packaging?.box_height_cm || "",
            package_type: initial?.packaging?.package_type || (isWine ? "Garrafa 750ml" : "Frasco vidro"),
            fragile: initial?.packaging?.fragile ?? isWine,
        },
    });

    // Linked price editors
    function setSalePrice(v) {
        const price = parseFloat(v) || 0;
        const ratio = form.price > 0 && form.producer_price ? form.producer_price / form.price : 0.85;
        setForm({ ...form, price: v, producer_price: price > 0 ? (price * ratio).toFixed(2) : "" });
    }
    function setProducerPrice(v) {
        const prod = parseFloat(v) || 0;
        const sale = parseFloat(form.price) || 0;
        if (prod > sale) setForm({ ...form, producer_price: v, price: v });
        else setForm({ ...form, producer_price: v });
    }
    function setCommission(v) {
        const comm = parseFloat(v) || 0;
        const sale = parseFloat(form.price) || 0;
        setForm({ ...form, producer_price: Math.max(0, sale - comm).toFixed(2) });
    }

    async function submit(e) {
        e.preventDefault();
        if (!form.name || !form.price || form.stock === "") {
            toast.error("Preencha nome, preço e stock");
            return;
        }
        if (form.images.length === 0) {
            toast.error("Adicione pelo menos uma fotografia");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                short_description: form.short_description || undefined,
                description: form.description,
                category: form.category,
                region: form.region,
                price: parseFloat(form.price),
                producer_price: form.producer_price ? parseFloat(form.producer_price) : undefined,
                stock: parseInt(form.stock),
                limited_edition: form.limited_edition,
                images: form.images,
                kind,
            };
            if (isWine) {
                payload.wine = {
                    ...form.wine,
                    vintage_year: form.wine.vintage_year ? parseInt(form.wine.vintage_year) : undefined,
                    alcohol_pct: form.wine.alcohol_pct ? parseFloat(form.wine.alcohol_pct) : undefined,
                    bottle_volume_ml: form.wine.bottle_volume_ml ? parseInt(form.wine.bottle_volume_ml) : undefined,
                };
            } else {
                payload.product = {
                    ...form.product,
                    shelf_life_days: form.product.shelf_life_days ? parseInt(form.product.shelf_life_days) : undefined,
                };
            }
            payload.packaging = {
                ...form.packaging,
                unit_weight_kg: form.packaging.unit_weight_kg ? parseFloat(form.packaging.unit_weight_kg) : undefined,
                unit_length_cm: form.packaging.unit_length_cm ? parseFloat(form.packaging.unit_length_cm) : undefined,
                unit_width_cm: form.packaging.unit_width_cm ? parseFloat(form.packaging.unit_width_cm) : undefined,
                unit_height_cm: form.packaging.unit_height_cm ? parseFloat(form.packaging.unit_height_cm) : undefined,
                box_units: parseInt(form.packaging.box_units) || 1,
                box_weight_kg: form.packaging.box_weight_kg ? parseFloat(form.packaging.box_weight_kg) : undefined,
                box_length_cm: form.packaging.box_length_cm ? parseFloat(form.packaging.box_length_cm) : undefined,
                box_width_cm: form.packaging.box_width_cm ? parseFloat(form.packaging.box_width_cm) : undefined,
                box_height_cm: form.packaging.box_height_cm ? parseFloat(form.packaging.box_height_cm) : undefined,
            };
            if (initial) {
                await api.patch(`/products/${initial.id}`, payload);
                toast.success("Produto atualizado");
            } else {
                await api.post("/products", payload);
                toast.success("Produto submetido — a aguardar aprovação");
            }
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-cream overflow-y-auto" data-testid="product-form-modal">
            <div className="min-h-full">
                {/* Sticky header */}
                <div className="sticky top-0 z-10 bg-cream border-b border-wine-dark/10">
                    <div className="max-w-5xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between">
                        <div>
                            <span className="label-overline text-wine inline-flex items-center gap-1.5">
                                {isWine ? <Wine className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                                {initial ? "Editar" : "Adicionar"}
                            </span>
                            <h2 className="font-heading text-2xl text-wine-dark mt-0.5">{isWine ? "Vinho" : "Produto Artesanal"}</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={onClose} className="text-wine-dark/60 hover:text-wine p-2 rounded-md">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>

                <form onSubmit={submit} className="max-w-5xl mx-auto px-6 md:px-8 py-8 space-y-6" data-testid="vendor-product-form">
                    {/* 1. Identidade */}
                    <Section icon={isWine ? Wine : Package} title="Identidade" defaultOpen>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Nome do produto" value={form.name} set={(v) => setForm({ ...form, name: v })} required testId="field-name" />
                            <SelectField
                                label="Categoria"
                                value={form.category}
                                set={(v) => setForm({ ...form, category: v })}
                                options={isWine ? WINE_CATEGORIES : PRODUCT_CATEGORIES}
                                testId="field-category"
                            />
                            <SelectField label="Região" value={form.region} set={(v) => setForm({ ...form, region: v })} options={REGIONS} testId="field-region" />
                            <Field label="Descrição curta (1 frase)" value={form.short_description} set={(v) => setForm({ ...form, short_description: v })} placeholder="ex: Tinto encorpado com aromas de frutos vermelhos" />
                        </div>
                        <TextArea label="Descrição completa" value={form.description} set={(v) => setForm({ ...form, description: v })} rows={4} placeholder="Conte a história do produto, processo, sabores…" required />
                    </Section>

                    {/* 2. Fotografias */}
                    <Section icon={ImageIcon} title="Fotografias" defaultOpen badge={`${form.images.length}/8`}>
                        <ImageUploader
                            images={form.images}
                            onChange={(imgs) => setForm({ ...form, images: imgs })}
                            max={8}
                        />
                    </Section>

                    {/* 3. Preço & Stock */}
                    <Section icon={Coins} title="Preço & Stock" defaultOpen>
                        <div className="bg-cream rounded-md p-4 border border-gold-soft/40 mb-4">
                            <div className="flex items-center justify-between mb-3">
                                <span className="label-overline text-wine">Preços (interligados)</span>
                                <span className="text-[10px] text-wine-dark/55">Editar um campo recalcula os outros</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <PriceField label="Valor produtor (€)" value={form.producer_price} set={setProducerPrice} color="vineyard" testId="field-producer-price" />
                                <PriceField label="Comissão (€)" value={form.price && form.producer_price ? (parseFloat(form.price) - parseFloat(form.producer_price)).toFixed(2) : ""} set={setCommission} color="gold-deep" testId="field-commission" />
                                <PriceField label="Preço venda (€)" value={form.price} set={setSalePrice} color="wine" testId="field-sale-price" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Stock disponível" value={form.stock} set={(v) => setForm({ ...form, stock: v })} type="number" required testId="field-stock" />
                            <label className="flex items-center gap-2 mt-7">
                                <input type="checkbox" checked={form.limited_edition} onChange={(e) => setForm({ ...form, limited_edition: e.target.checked })} className="accent-wine w-4 h-4" />
                                <span className="text-sm text-wine-dark">Edição limitada</span>
                            </label>
                        </div>
                    </Section>

                    {/* 4. Embalagem & Transporte */}
                    <Section icon={Box} title="Embalagem & Transporte" defaultOpen>
                        <p className="text-xs text-wine-dark/60 mb-4">Estas medidas são essenciais para o cálculo do frete pela transportadora.</p>

                        <SelectField label="Tipo de embalagem" value={form.packaging.package_type} set={(v) => setForm({ ...form, packaging: { ...form.packaging, package_type: v } })} options={isWine ? PACKAGE_TYPES.wine : PACKAGE_TYPES.product} />

                        <h4 className="font-heading text-base text-wine-dark mt-5 mb-2">Unidade individual</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <NumField label="Peso (kg)" value={form.packaging.unit_weight_kg} set={(v) => setForm({ ...form, packaging: { ...form.packaging, unit_weight_kg: v } })} step="0.01" testId="field-unit-weight" />
                            <NumField label="Comprimento (cm)" value={form.packaging.unit_length_cm} set={(v) => setForm({ ...form, packaging: { ...form.packaging, unit_length_cm: v } })} />
                            <NumField label="Largura (cm)" value={form.packaging.unit_width_cm} set={(v) => setForm({ ...form, packaging: { ...form.packaging, unit_width_cm: v } })} />
                            <NumField label="Altura (cm)" value={form.packaging.unit_height_cm} set={(v) => setForm({ ...form, packaging: { ...form.packaging, unit_height_cm: v } })} />
                        </div>

                        <h4 className="font-heading text-base text-wine-dark mt-5 mb-2">Caixa de envio (opcional)</h4>
                        <p className="text-[11px] text-wine-dark/55 mb-3">Se costuma agrupar várias unidades numa caixa (ex: 6 garrafas), indique aqui — o sistema usa estas dimensões para envios em quantidade.</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <NumField label="Unidades/caixa" value={form.packaging.box_units} set={(v) => setForm({ ...form, packaging: { ...form.packaging, box_units: v } })} testId="field-box-units" />
                            <NumField label="Peso caixa (kg)" value={form.packaging.box_weight_kg} set={(v) => setForm({ ...form, packaging: { ...form.packaging, box_weight_kg: v } })} step="0.01" />
                            <NumField label="Compr. (cm)" value={form.packaging.box_length_cm} set={(v) => setForm({ ...form, packaging: { ...form.packaging, box_length_cm: v } })} />
                            <NumField label="Largura (cm)" value={form.packaging.box_width_cm} set={(v) => setForm({ ...form, packaging: { ...form.packaging, box_width_cm: v } })} />
                            <NumField label="Altura (cm)" value={form.packaging.box_height_cm} set={(v) => setForm({ ...form, packaging: { ...form.packaging, box_height_cm: v } })} />
                        </div>

                        <label className="flex items-center gap-2 mt-4">
                            <input type="checkbox" checked={form.packaging.fragile} onChange={(e) => setForm({ ...form, packaging: { ...form.packaging, fragile: e.target.checked } })} className="accent-wine w-4 h-4" />
                            <span className="text-sm text-wine-dark inline-flex items-center gap-1.5">
                                <AlertCircle className="h-3.5 w-3.5 text-destructive" /> Produto frágil (requer manuseamento cuidado)
                            </span>
                        </label>
                    </Section>

                    {/* 5a. Wine details */}
                    {isWine && (
                        <Section icon={Wine} title="Detalhes do Vinho" defaultOpen>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <SelectField label="Tipo" value={form.wine.wine_type} set={(v) => setForm({ ...form, wine: { ...form.wine, wine_type: v } })} options={WINE_TYPES.map((t) => t.value)} renderOption={(v) => WINE_TYPES.find((t) => t.value === v)?.label || v} />
                                <NumField label="Ano / Colheita" value={form.wine.vintage_year} set={(v) => setForm({ ...form, wine: { ...form.wine, vintage_year: v } })} placeholder="2022" />
                                <NumField label="Teor de álcool (%)" value={form.wine.alcohol_pct} set={(v) => setForm({ ...form, wine: { ...form.wine, alcohol_pct: v } })} step="0.1" placeholder="13.5" />
                                <SelectField label="Classificação" value={form.wine.classification} set={(v) => setForm({ ...form, wine: { ...form.wine, classification: v } })} options={["", ...CLASSIFICATIONS]} renderOption={(v) => v || "—"} />
                                <NumField label="Volume garrafa (ml)" value={form.wine.bottle_volume_ml} set={(v) => setForm({ ...form, wine: { ...form.wine, bottle_volume_ml: v } })} placeholder="750" />
                                <Field label="Temperatura de serviço" value={form.wine.serving_temp} set={(v) => setForm({ ...form, wine: { ...form.wine, serving_temp: v } })} placeholder="16-18°C" />
                            </div>
                            <TagsField label="Castas" values={form.wine.grape_varieties} set={(arr) => setForm({ ...form, wine: { ...form.wine, grape_varieties: arr } })} placeholder="ex: Touriga Nacional, Tinta Roriz…" />
                            <TextArea label="Harmonização (pairing)" value={form.wine.pairing} set={(v) => setForm({ ...form, wine: { ...form.wine, pairing: v } })} rows={2} placeholder="Ideal para acompanhar carnes vermelhas, queijos curados…" />
                        </Section>
                    )}

                    {/* 5b. Product details */}
                    {!isWine && (
                        <Section icon={Package} title="Detalhes do Produto" defaultOpen>
                            <TextArea label="Ingredientes" value={form.product.ingredients} set={(v) => setForm({ ...form, product: { ...form.product, ingredients: v } })} rows={2} placeholder="Liste os ingredientes em ordem decrescente de peso" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                <NumField label="Validade (dias)" value={form.product.shelf_life_days} set={(v) => setForm({ ...form, product: { ...form.product, shelf_life_days: v } })} placeholder="365" />
                                <Field label="Conservação" value={form.product.storage} set={(v) => setForm({ ...form, product: { ...form.product, storage: v } })} placeholder="Local fresco e seco, ao abrigo da luz" />
                            </div>
                            <MultiTagPicker label="Alergéneos" all={ALLERGENS} selected={form.product.allergens} set={(arr) => setForm({ ...form, product: { ...form.product, allergens: arr } })} />
                            <MultiTagPicker label="Certificações" all={CERTIFICATIONS} selected={form.product.certifications} set={(arr) => setForm({ ...form, product: { ...form.product, certifications: arr } })} />
                        </Section>
                    )}

                    {/* Submit */}
                    <div className="sticky bottom-0 -mx-6 md:-mx-8 px-6 md:px-8 py-4 bg-cream border-t border-wine-dark/10 flex items-center justify-between">
                        <p className="text-[11px] text-wine-dark/55">{initial ? "As alterações são guardadas imediatamente." : "Após submeter, o produto aguarda aprovação do administrador."}</p>
                        <div className="flex gap-2">
                            <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs text-wine-dark/60 hover:text-wine label-overline">Cancelar</button>
                            <button type="submit" disabled={saving} className="btn-wine inline-flex items-center gap-2 disabled:opacity-50" data-testid="submit-product">
                                <Save className="h-4 w-4" /> {saving ? "A guardar…" : initial ? "Atualizar" : "Submeter"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ==== Sub-components ====

function Section({ icon: Icon, title, defaultOpen = false, badge, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-white rounded-md border border-gold-soft/30">
            <button type="button" onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-cream rounded-t-md">
                <span className="font-heading text-lg text-wine-dark inline-flex items-center gap-2">
                    <Icon className="h-4 w-4 text-wine" strokeWidth={1.5} /> {title}
                    {badge && <span className="text-[10px] label-overline bg-gold-soft/20 text-gold-deep px-2 py-0.5 rounded ml-2">{badge}</span>}
                </span>
                {open ? <ChevronUp className="h-4 w-4 text-wine-dark/40" /> : <ChevronDown className="h-4 w-4 text-wine-dark/40" />}
            </button>
            {open && <div className="p-5 pt-2">{children}</div>}
        </div>
    );
}

function Field({ label, value, set, type = "text", placeholder, required, testId }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark/65 text-[10px] mb-1.5 block">{label}{required && " *"}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => set(e.target.value)}
                required={required}
                placeholder={placeholder}
                className="w-full px-3 py-2.5 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
                data-testid={testId}
            />
        </label>
    );
}

function NumField({ label, value, set, step = "1", placeholder, testId }) {
    return <Field label={label} value={value} set={set} type="number" placeholder={placeholder} testId={testId} />;
}

function TextArea({ label, value, set, rows = 3, placeholder, required }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark/65 text-[10px] mb-1.5 block">{label}{required && " *"}</span>
            <textarea
                value={value}
                onChange={(e) => set(e.target.value)}
                required={required}
                rows={rows}
                placeholder={placeholder}
                className="w-full px-3 py-2.5 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
            />
        </label>
    );
}

function SelectField({ label, value, set, options, renderOption, testId }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark/65 text-[10px] mb-1.5 block">{label}</span>
            <select
                value={value}
                onChange={(e) => set(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
                data-testid={testId}
            >
                {options.map((opt) => (
                    <option key={opt} value={opt}>{renderOption ? renderOption(opt) : opt}</option>
                ))}
            </select>
        </label>
    );
}

function PriceField({ label, value, set, color, testId }) {
    const colorClass = {
        vineyard: "border-vineyard/40 focus:border-vineyard",
        "gold-deep": "border-gold-deep/40 focus:border-gold-deep",
        wine: "border-wine/40 focus:border-wine",
    }[color] || "border-wine-dark/15 focus:border-wine";
    return (
        <label className="block">
            <span className="label-overline text-wine-dark/65 text-[10px] mb-1.5 block">{label}</span>
            <input
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => set(e.target.value)}
                className={`w-full px-3 py-2.5 bg-white border ${colorClass} rounded-md text-sm`}
                data-testid={testId}
            />
        </label>
    );
}

function TagsField({ label, values, set, placeholder }) {
    const [input, setInput] = useState("");
    function add() {
        const v = input.trim();
        if (!v) return;
        if (!values.includes(v)) set([...values, v]);
        setInput("");
    }
    return (
        <div className="mt-3">
            <span className="label-overline text-wine-dark/65 text-[10px] mb-1.5 block">{label}</span>
            <div className="flex flex-wrap gap-2 mb-2">
                {values.map((v) => (
                    <span key={v} className="inline-flex items-center gap-1 bg-wine/10 text-wine-dark text-xs px-3 py-1 rounded-full">
                        {v}
                        <button type="button" onClick={() => set(values.filter((x) => x !== v))} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder={placeholder}
                    className="flex-1 px-3 py-2 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
                />
                <button type="button" onClick={add} className="btn-wine-outline text-xs px-4">Adicionar</button>
            </div>
        </div>
    );
}

function MultiTagPicker({ label, all, selected, set }) {
    function toggle(v) {
        if (selected.includes(v)) set(selected.filter((x) => x !== v));
        else set([...selected, v]);
    }
    return (
        <div className="mt-3">
            <span className="label-overline text-wine-dark/65 text-[10px] mb-1.5 block">{label}</span>
            <div className="flex flex-wrap gap-2">
                {all.map((v) => {
                    const on = selected.includes(v);
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => toggle(v)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-wine text-cream border-wine" : "bg-white text-wine-dark border-wine-dark/15 hover:border-wine/40"}`}
                        >
                            {v}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function ImageUploader({ images, onChange, max }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    async function handleFiles(files) {
        const arr = Array.from(files).slice(0, max - images.length);
        if (arr.length === 0) return;
        setUploading(true);
        const next = [...images];
        for (const file of arr) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error(`${file.name}: maior que 5MB`);
                continue;
            }
            try {
                // Convert to base64 (Kubernetes-safe, no multipart needed)
                const data_base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result.split(",")[1]);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
                const { data } = await api.post("/vendor/upload-image", {
                    filename: file.name,
                    mime_type: file.type,
                    data_base64,
                });
                next.push(data.url);
            } catch (e) {
                toast.error(`${file.name}: ${e.response?.data?.detail || "falha no upload"}`);
            }
        }
        onChange(next);
        setUploading(false);
    }

    function onDrop(e) {
        e.preventDefault();
        if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
    }

    function move(from, to) {
        if (to < 0 || to >= images.length) return;
        const next = [...images];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChange(next);
    }

    return (
        <div>
            <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-wine-dark/20 hover:border-wine/50 rounded-md p-6 text-center cursor-pointer transition-colors bg-cream/50"
                data-testid="image-dropzone"
            >
                <Upload className="h-8 w-8 text-wine/40 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm text-wine-dark/70">{uploading ? "A enviar…" : "Arrastar e largar ou clicar para escolher"}</p>
                <p className="text-[11px] text-wine-dark/45 mt-1">JPG, PNG ou WebP · até 5MB cada · máx {max} fotos</p>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="image-input"
                />
            </div>

            {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    {images.map((url, i) => {
                        const fullUrl = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                        return (
                            <div key={i} className="relative group" data-testid={`image-${i}`}>
                                <img src={fullUrl} alt="" className="w-full h-32 object-cover rounded-md border border-wine-dark/10" />
                                {i === 0 && <span className="absolute top-1 left-1 bg-wine text-cream text-[9px] label-overline px-1.5 py-0.5 rounded">Principal</span>}
                                <div className="absolute inset-0 bg-elegant/0 group-hover:bg-elegant/60 transition-colors rounded-md flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                    {i > 0 && (
                                        <button type="button" onClick={() => move(i, i - 1)} className="bg-cream text-wine p-1.5 rounded text-xs">←</button>
                                    )}
                                    <button type="button" onClick={() => onChange(images.filter((_, idx) => idx !== i))} className="bg-destructive text-cream p-1.5 rounded">
                                        <X className="h-3 w-3" />
                                    </button>
                                    {i < images.length - 1 && (
                                        <button type="button" onClick={() => move(i, i + 1)} className="bg-cream text-wine p-1.5 rounded text-xs">→</button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
